import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Section } from '~/ui/Section.js';
import type { OfferReturn } from './MemberGate.js';
import {
  AutoPlayForcedSwitch,
  BoardFinishRow,
  BotSpeedChoice,
  CardBackRow,
  CardFrontRow,
  ConfirmPlaySwitch,
  CrossPositionChoice,
  DeckChoice,
  HapticsSwitch,
  HighContrastSwitch,
  ReducedMotionSwitch,
  SoundSwitch,
  VolumeLevel,
} from './PreferenceRows.js';

/**
 * The Game and Look and sound groups of Settings.
 *
 * Every row here is a preference the device already keeps, drawn in the groups
 * the settings sketch settled (`lobby-settings`, 2026-09-21). The card and
 * board pickers wear the members-only tags of its `game-paywall` variant, and
 * their See the offer chips come back here.
 *
 * The table's drawer still draws `SettingsList`, one flat list, because a
 * player mid-hand wants every switch in reach rather than a menu of groups.
 * The rows are shared (`PreferenceRows.tsx`), so the two cannot drift.
 */

/** The Game group's own address, where Back on the offer returns. */
const GAME_PATH = '/settings/game';

export function GameSettings(): React.JSX.Element {
  const { t } = useTranslation();
  const arrival = useLocation().state as { returnTo?: unknown } | null;
  const offer: OfferReturn = {
    backTo: GAME_PATH,
    returnTo: typeof arrival?.returnTo === 'string' ? arrival.returnTo : undefined,
  };
  return (
    <>
      <Section title={t('settings.cards')}>
        <DeckChoice />
        <CardFrontRow offer={offer} />
        <CardBackRow offer={offer} />
      </Section>
      <Section title={t('settings.board')}>
        <BoardFinishRow offer={offer} />
      </Section>
      <Section title={t('settings.sectionPlay')}>
        <ConfirmPlaySwitch />
        <AutoPlayForcedSwitch />
        <BotSpeedChoice />
      </Section>
      <Section title={t('setup.cross')}>
        <CrossPositionChoice />
      </Section>
    </>
  );
}

export function LookSoundSettings(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <Section title={t('settings.sectionSound')}>
        <SoundSwitch />
        <VolumeLevel />
      </Section>
      <Section title={t('settings.look')}>
        <HighContrastSwitch />
      </Section>
      <Section title={t('settings.motion')}>
        <ReducedMotionSwitch />
      </Section>
      <Section title={t('settings.haptics')}>
        <HapticsSwitch />
      </Section>
    </>
  );
}
